import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, FileText, Eye, Sparkles } from 'lucide-react';
import { searchApi } from '../lib/api';
import { formatDate, formatCurrency } from '../utils/format';

// Common search patterns
const QUICK_SEARCHES = [
  {
    label: 'Find Insurance Cards',
    query: 'insurance card',
    description: 'Find all insurance documents',
  },
  {
    label: 'Find by VIN',
    query: 'VIN',
    description: 'Search for vehicle documents by VIN number',
  },
  {
    label: 'Tax Documents',
    query: 'tax 1099 W2',
    description: 'Find all tax-related documents',
  },
  {
    label: 'Equipment Manuals',
    query: 'manual instructions',
    description: 'Find user manuals and instructions',
  },
  {
    label: 'Large Purchases',
    query: '',
    description: 'Receipts over $500',
    filter: { minAmount: 500 },
  },
];

export default function SmartSearch() {
  const [query, setQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    vendor: '',
    minAmount: '',
    maxAmount: '',
    dateFrom: '',
    dateTo: '',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['smart-search', searchQuery, filters],
    queryFn: () => {
      const params: any = { q: searchQuery };
      
      if (filters.category) params.category = filters.category;
      if (filters.vendor) params.vendor = filters.vendor;
      if (filters.minAmount) params.minAmount = filters.minAmount;
      if (filters.maxAmount) params.maxAmount = filters.maxAmount;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      
      return searchApi.search(params);
    },
    enabled: searchQuery.length > 0 || Object.values(filters).some(v => v !== ''),
  });

  const documents = data?.data?.documents || [];
  const total = data?.data?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(query);
  };

  const handleQuickSearch = (quickSearch: typeof QUICK_SEARCHES[0]) => {
    setQuery(quickSearch.query);
    setSearchQuery(quickSearch.query);
    if (quickSearch.filter) {
      setFilters({ ...filters, minAmount: String(quickSearch.filter.minAmount || '') });
    }
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      vendor: '',
      minAmount: '',
      maxAmount: '',
      dateFrom: '',
      dateTo: '',
    });
    setQuery('');
    setSearchQuery('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Smart Search</h1>
        <p className="text-gray-600">
          Find any document instantly - insurance cards, receipts, manuals, VIN numbers, and more
        </p>
      </div>

      {/* Quick Search Buttons */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-yellow-500" />
          <h3 className="font-semibold text-gray-900">Quick Searches</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_SEARCHES.map((qs, index) => (
            <button
              key={index}
              onClick={() => handleQuickSearch(qs)}
              className="btn-secondary text-sm"
              title={qs.description}
            >
              {qs.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="card">
        <div className="space-y-4">
          {/* Main Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by vendor, VIN, amount, description, or any text..."
              className="input pl-10"
            />
          </div>

          {/* Advanced Filters */}
          <details className="group">
            <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
              Advanced Filters
            </summary>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="label">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="input"
                >
                  <option value="">All Categories</option>
                  <option value="Receipts">Receipts</option>
                  <option value="Invoices">Invoices</option>
                  <option value="Insurance">Insurance</option>
                  <option value="Equipment Manuals">Manuals</option>
                  <option value="Tax Documents">Tax Documents</option>
                  <option value="Contracts">Contracts</option>
                </select>
              </div>

              <div>
                <label className="label">Vendor</label>
                <input
                  type="text"
                  placeholder="e.g., John Deere"
                  value={filters.vendor}
                  onChange={(e) => setFilters({ ...filters, vendor: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="label">Min Amount</label>
                <input
                  type="number"
                  placeholder="$0"
                  value={filters.minAmount}
                  onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="label">Max Amount</label>
                <input
                  type="number"
                  placeholder="$10,000"
                  value={filters.maxAmount}
                  onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="label">From Date</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  className="input"
                />
              </div>

              <div>
                <label className="label">To Date</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  className="input"
                />
              </div>
            </div>
          </details>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button type="submit" className="btn-primary">
              Search
            </button>
            <button type="button" onClick={clearFilters} className="btn-secondary">
              Clear All
            </button>
          </div>
        </div>
      </form>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      ) : searchQuery || Object.values(filters).some(v => v !== '') ? (
        <div className="card">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Found <span className="font-semibold">{total}</span> document{total !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>

          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No documents found</p>
              <p className="text-sm text-gray-400 mt-2">
                Try different keywords or adjust your filters
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {documents.map((doc: any) => (
                <div
                  key={doc.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <h3 className="font-medium text-gray-900">{doc.filename}</h3>
                        <span className="badge-gray uppercase text-xs">
                          {doc.file_type}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        {doc.vendor_name && (
                          <span className="font-medium">Vendor: {doc.vendor_name}</span>
                        )}
                        {doc.total_amount && (
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(doc.total_amount)}
                          </span>
                        )}
                        {doc.document_date && (
                          <span>{formatDate(doc.document_date)}</span>
                        )}
                      </div>

                      {/* Show OCR preview if available */}
                      {doc.ocr_text && (
                        <p className="text-xs text-gray-500 line-clamp-2">
                          {doc.ocr_text.substring(0, 200)}...
                        </p>
                      )}
                    </div>

                    <Link
                      to={`/documents/${doc.id}`}
                      className="btn-secondary flex items-center gap-2 ml-4"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="card text-center py-12">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-2">
            Enter a search query or use advanced filters
          </p>
          <p className="text-sm text-gray-400">
            Try searching for: insurance cards, VIN numbers, vendor names, amounts, or any text
          </p>
        </div>
      )}

      {/* Search Examples */}
      <div className="card bg-green-50 border-green-200">
        <h3 className="text-sm font-semibold text-green-900 mb-2">
          Search Examples
        </h3>
        <ul className="text-sm text-green-800 space-y-1">
          <li>• <strong>"insurance card truck"</strong> - Find your truck insurance cards</li>
          <li>• <strong>"VIN 1HGBH41JXMN109186"</strong> - Find documents for specific vehicle</li>
          <li>• <strong>"John Deere"</strong> - All John Deere receipts and documents</li>
          <li>• <strong>"manual 325D"</strong> - Find equipment manual by model number</li>
          <li>• <strong>Filter: Min Amount $500</strong> - All purchases over $500</li>
          <li>• <strong>"check 1234"</strong> - Find receipts paid with specific check</li>
          <li>• <strong>"tax 2023"</strong> - All tax documents from 2023</li>
          <li>• <strong>"warranty"</strong> - Find all warranty documents</li>
        </ul>
      </div>
    </div>
  );
}

